/**
 * Product page art (T854), ported from docs/design/html/lib/illustrations.mjs and web-landing-product-*.mjs.
 * Same rules as home-art.tsx: inline SVG, token classes, decorative art aria-hidden, explicit text colours so
 * light mode is readable, and no animation (the design's looping demos are shown as their final frame).
 */
import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

import { useLocale } from "@/core/i18n/locale-context"

import {
  INVOICE_EXAMPLE,
  MILEAGE_EXAMPLE,
  RECEIPT_EXAMPLE,
} from "../../content/products/example-numbers"
import { card, s, Scene, t } from "../home/home-art"

const tones = {
  primary: "bg-primary/10 text-primary",
  success: "bg-success/15 text-success",
  warning: "bg-warning-soft text-warning",
  destructive: "bg-destructive/15 text-destructive",
} as const

/** Small rounded tile with an icon. Decorative: the text next to it carries the meaning. */
export const IconTile = ({
  icon: Icon,
  tone = "primary",
  className = "",
}: {
  icon: LucideIcon
  tone?: keyof typeof tones
  className?: string
}) => (
  <span
    aria-hidden="true"
    // Test hook: the tile is decorative and has no role to query by (T855).
    data-icon-tile=""
    className={`grid size-10 shrink-0 place-items-center rounded-lg ${tones[tone]} ${className}`}
  >
    <Icon className="size-5" />
  </span>
)

/** The 12-week logbook strip: done weeks, the current week raised and labelled, weeks to go. */
export const LogbookIllustration = ({
  vehicle = "Toyota Corolla",
  week = 4,
  weeks = 12,
  range = "5–11 Oct",
}: {
  vehicle?: string
  week?: number
  weeks?: number
  range?: string
}) => {
  const zh = useLocale() === "zh"
  const cx = 24 + (week - 1) * 23 + 9.5
  const bx = Math.min(Math.max(12, cx - 30), 112)
  return (
    <Scene viewBox="0 0 320 206">
      <rect x="1" y="1" width="318" height="204" rx="12" className={card} strokeWidth="1.5" />
      <text x="20" y="26" className={t}>
        {zh ? "行车日志" : "Logbook"} · {vehicle}
      </text>
      <text x="20" y="46" className={s}>
        {zh ? "开始于 9月14日（周一） · 46,980 km" : "Started Mon 14 Sep · 46,980 km"}
      </text>
      {Array.from({ length: weeks }, (_, k) => {
        const n = k + 1
        const on = n === week
        const x = 24 + k * 23
        return (
          <g key={n}>
            <rect
              x={x}
              y={on ? 66 : 74}
              width="19"
              height={on ? 52 : 36}
              rx="5"
              className={on ? "fill-primary" : n < week ? "fill-success" : "fill-muted"}
            />
            <text x={x + 9.5} y="132" textAnchor="middle" className={on ? t : s}>
              {n}
            </text>
          </g>
        )
      })}
      <path d={`M${cx - 6} 146l6-6 6 6z`} className="fill-primary/15 stroke-primary" />
      <rect
        x={bx}
        y="146"
        width="196"
        height="28"
        rx="8"
        className="fill-primary/15 stroke-primary"
      />
      <text x={bx + 12} y="165" className={t}>
        {zh ? `第 ${week} 周，共 ${weeks} 周 · ${range}` : `Week ${week} of ${weeks} · ${range}`}
      </text>
      <text x="20" y="196" className={s}>
        {zh
          ? `已记录第 1–${week - 1} 周 · 还剩 ${weeks - week} 周`
          : `Weeks 1–${week - 1} recorded · ${weeks - week} to go`}
      </text>
    </Scene>
  )
}

/** One labelled picture; its inner mock is hidden from assistive tech. */
const HeroFrame = ({
  label,
  kind,
  children,
}: {
  label: string
  kind: "phone" | "browser"
  children: ReactNode
}) => (
  <div
    role="img"
    aria-label={label}
    className={`border-border bg-card text-foreground mx-auto w-full shadow-lg ${
      kind === "phone"
        ? "max-w-[300px] rounded-[2rem] border-4 p-2"
        : "max-w-[460px] rounded-xl border"
    }`}
  >
    {kind === "browser" && (
      <div
        aria-hidden="true"
        className="border-border text-muted-foreground flex items-center gap-1.5 border-b px-3 py-2 text-xs"
      >
        <span className="bg-muted size-2.5 rounded-full" />
        <span className="bg-muted size-2.5 rounded-full" />
        <span className="bg-muted size-2.5 rounded-full" />
        <span className="ml-2">Invoices · New invoice</span>
      </div>
    )}
    <div aria-hidden="true" className="bg-background grid gap-3 rounded-3xl p-4">
      {children}
    </div>
  </div>
)

/** Receipt scan result on a phone: four fields read, each confirmed, GST highlighted. Final frame of demoScan. */
export const ScanHeroMock = () => {
  const zh = useLocale() === "zh"
  const r = RECEIPT_EXAMPLE
  const fields = [
    ["Merchant", r.merchant1],
    ["Date", "Wed 7 Oct 2026"],
    ["Total", r.total1],
    ["GST", r.gst1],
  ] as const
  return (
    <HeroFrame
      kind="phone"
      label={
        zh
          ? `收据扫描示例：${r.merchant1}，日期、总额和 GST 会被读出，由你逐项确认。总额 ${r.total1}，其中 GST ${r.gst1}。`
          : `Example receipt scan: ${r.merchant1}, date, total and GST are read and you confirm each one. GST ${r.gst1} of ${r.total1}.`
      }
    >
      <div className="text-muted-foreground flex items-center justify-between text-sm">
        <b className="text-foreground">Scan receipt</b>
        <span>Expenses</span>
      </div>
      <ul className="grid gap-2">
        {fields.map(([k, v]) => (
          <li
            key={k}
            className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm ${
              k === "GST" ? "border-primary bg-primary/10" : "border-border bg-card"
            }`}
          >
            <span>
              <span className="text-muted-foreground block text-xs">{k}</span>
              <b className="text-foreground">{v}</b>
            </span>
            <span className="bg-success text-background grid size-5 place-items-center rounded-full text-xs">
              ✓
            </span>
          </li>
        ))}
      </ul>
      <p className="bg-success/15 text-success rounded-lg px-3 py-2 text-center text-sm font-medium">
        You confirm each one
      </p>
    </HeroFrame>
  )
}

/** New-invoice form in a browser frame, after Send. Final frame of demoInvoice. */
export const InvoiceHeroMock = () => {
  const zh = useLocale() === "zh"
  const e = INVOICE_EXAMPLE
  return (
    <HeroFrame
      kind="browser"
      label={
        zh
          ? `开给 ${e.customer} 的发票示例：${e.lines.map((l) => `${l.description} ${l.amount}`).join("，")}，GST ${e.gst}，合计 ${e.total}。`
          : `Example invoice to ${e.customer}: ${e.lines.map((l) => `${l.description} ${l.amount}`).join(", ")}, GST ${e.gst}, total ${e.total}.`
      }
    >
      <div className="flex items-center justify-between text-sm">
        <b className="text-foreground">New invoice</b>
        <span className="bg-success/15 text-success rounded-full px-2 py-0.5 text-xs font-medium">
          Sent
        </span>
      </div>
      <p className="text-muted-foreground text-sm">
        Customer: <b className="text-foreground">{e.customer}</b>
      </p>
      <ul className="divide-border divide-y text-sm">
        {e.lines.map((l) => (
          <li key={l.description} className="flex justify-between py-1.5">
            <span className="text-muted-foreground">{l.description}</span>
            <b className="text-foreground tabular-nums">{l.amount}</b>
          </li>
        ))}
      </ul>
      <dl className="grid gap-1 text-sm tabular-nums">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Subtotal</dt>
          <dd className="text-foreground font-semibold">{e.subtotal}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">GST</dt>
          <dd className="text-foreground font-semibold">{e.gst}</dd>
        </div>
        <div className="border-border flex justify-between border-t pt-1 text-base">
          <dt className="text-foreground font-bold">Total</dt>
          <dd className="text-foreground font-bold">{e.total}</dd>
        </div>
      </dl>
    </HeroFrame>
  )
}

const mockBox = "border-border bg-card text-foreground grid gap-2 rounded-xl border p-4 text-sm"

/** Compare-methods table as a picture (the real table is in the text of the page). */
export const MethodsMock = () => {
  const m = MILEAGE_EXAMPLE
  return (
    <div aria-hidden="true" className={mockBox}>
      <b>Compare methods</b>
      <div className="grid grid-cols-3 gap-2 tabular-nums">
        <span className="text-muted-foreground">Example year</span>
        <b>Cents per km</b>
        <b>Logbook</b>
        <span className="text-muted-foreground">Business km</span>
        <span>{m.businessKm}</span>
        <span>{m.businessKm}</span>
        <span className="text-muted-foreground">Estimate</span>
        <b className="text-primary">{m.centsPerKm}</b>
        <b className="text-primary">{m.logbook}</b>
      </div>
    </div>
  )
}

export interface MockRow {
  title: string
  sub: string
  badge: string
  tone?: keyof typeof tones
}

/** A short list of rows with a status badge (review list, sent mail, payments). */
export const ListMock = ({ rows }: { rows: MockRow[] }) => (
  <div aria-hidden="true" className={mockBox}>
    {rows.map(({ title, sub, badge, tone = "primary" }) => (
      <div key={title} className="flex items-center justify-between gap-2">
        <span>
          <b className="block">{title}</b>
          <span className="text-muted-foreground">{sub}</span>
        </span>
        <span className={`${tones[tone]} shrink-0 rounded-full px-2 py-0.5 text-xs font-medium`}>
          {badge}
        </span>
      </div>
    ))}
  </div>
)

/**
 * Cents per km worked example (web-landing-seo-article `centsScene`): the km used against the cap, and the sum.
 * One labelled image (the figure caption repeats the numbers), unlike the decorative home scenes.
 */
export const CentsScene = ({
  km,
  capKm,
  rate,
  year,
  result,
}: {
  km: number
  capKm: number
  /** e.g. "91 c" */
  rate: string
  /** e.g. "FY 2026–27" */
  year: string
  /** Formatted deduction, e.g. "A$2,912.00" */
  result: string
}) => {
  const f = (v: number) => v.toLocaleString("en-AU")
  const x = 20 + (280 * Math.min(km, capKm)) / capKm
  return (
    <svg
      viewBox="0 0 320 186"
      role="img"
      aria-label={
        useLocale() === "zh"
          ? `cents per km：${f(km)} km，上限 ${f(capKm)} km，每公里 ${rate}，结果 ${result}`
          : `Cents per km: ${f(km)} km of the ${f(capKm)} km cap at ${rate}/km is ${result}`
      }
      className="h-auto w-full max-w-[420px]"
    >
      <text x="20" y="26" className={t}>
        Cents per km
      </text>
      <text x="300" y="26" textAnchor="end" className={s}>
        {rate}/km · {year}
      </text>
      <text x={x} y="60" textAnchor="middle" className={t}>
        {f(km)} km
      </text>
      <rect x="20" y="76" width="280" height="14" rx="7" className="fill-muted" />
      <rect x="20" y="76" width={x - 20} height="14" rx="7" className="fill-primary" />
      <path d="M300 68V98" className="stroke-muted-foreground" strokeWidth="2" />
      <circle cx={x} cy="83" r="9" className="fill-card stroke-primary" strokeWidth="4" />
      <text x="20" y="116" className={s}>
        0 km
      </text>
      <text x="300" y="116" textAnchor="end" className={t}>
        {f(capKm)} km cap
      </text>
      <rect x="20" y="134" width="280" height="40" rx="8" className="fill-primary/10" />
      <text x="160" y="159" textAnchor="middle" className={t}>
        {f(km)} km × {rate} = {result}
      </text>
    </svg>
  )
}
